import { useState, useEffect, useCallback, useRef } from 'react';
import { Video } from '../types/video'; // Import the shared Video interface
import { openDB, DBSchema, IDBPDatabase } from 'idb'; // Add IDBPDatabase to imports

// Add these type declarations at the top of the file
declare global {
  interface Window {
    _lastCacheLog?: number;
    _lastLoggedVideosCount?: number;
  }
}

// Extend the Window interface to include File System Access API
declare global {
  interface Window {
    showDirectoryPicker: (options?: {
      id?: string;
      startIn?: string;
      mode?: 'read' | 'readwrite';
    }) => Promise<FileSystemDirectoryHandle>;
  }
  
  interface FileSystemDirectoryHandle {
    values(): AsyncIterableIterator<FileSystemHandle>;
    requestPermission(options?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
  }

  interface FileSystemHandle {
    readonly kind: 'file' | 'directory';
    readonly name: string;
  }

  interface FileSystemFileHandle extends FileSystemHandle {
    readonly kind: 'file';
    getFile(): Promise<File>;
  }
}

// Define our database structure
interface ClipoDBSchema extends DBSchema {
  'file-handles': {
    key: string;
    value: FileSystemDirectoryHandle;
  };
}

// Fix the return type of getDB to use IDBPDatabase from idb library
const getDB = async (): Promise<IDBPDatabase<ClipoDBSchema>> => {
  // Force the database creation if it doesn't exist, and ensure the object store is created
  return openDB<ClipoDBSchema>('clipo-storage', 1, {
    upgrade(db) {
      console.log('Creating or upgrading database');
      // Always attempt to create the object store if it doesn't exist
      if (!db.objectStoreNames.contains('file-handles')) {
        console.log('Creating file-handles object store');
        db.createObjectStore('file-handles');
      }
    }
  });
};

// Update the storeDirectoryHandle function
const storeDirectoryHandle = async (handle: FileSystemDirectoryHandle) => {
  try {
    // Always get a fresh DB connection
    const db = await getDB();
    
    // Verify the object store exists before trying to use it
    if (!db.objectStoreNames.contains('file-handles')) {
      console.error('Object store file-handles still does not exist after DB initialization');
      // Try to recreate the database
      await deleteDatabase();
      // Get a new connection after recreation
      const newDb = await getDB();
      if (!newDb.objectStoreNames.contains('file-handles')) {
        throw new Error('Failed to create object store');
      }
      await newDb.put('file-handles', handle, 'selectedFolder');
    } else {
      await db.put('file-handles', handle, 'selectedFolder');
    }
    
    console.log('Directory handle stored in IndexedDB');
    return true;
  } catch (err) {
    console.error('Failed to store directory handle:', err);
    return false;
  }
};

// Update the retrieveDirectoryHandle function
const retrieveDirectoryHandle = async (): Promise<FileSystemDirectoryHandle | null> => {
  try {
    // Force database creation/opening to ensure it exists
    const db = await getDB();
    
    // Double check if the object store exists
    if (!db.objectStoreNames.contains('file-handles')) {
      console.log('Object store "file-handles" does not exist');
      
      // Try to recreate the database
      console.log('Attempting to recreate the database...');
      await deleteDatabase();
      
      // Try again after recreation
      const newDb = await getDB();
      if (!newDb.objectStoreNames.contains('file-handles')) {
        console.log('Failed to create object store after database recreation');
        return null;
      }
      
      // At this point, we have a newly created database with the object store,
      // but it won't have our data yet
      return null;
    }
    
    try {
      // Try to get the stored handle
      const handle = await db.get('file-handles', 'selectedFolder');
      if (handle) {
        console.log('Retrieved directory handle from IndexedDB');
        return handle;
      }
    } catch (err) {
      console.error('Error getting handle from object store:', err);
    }
    
    return null;
  } catch (err) {
    console.error('Failed to retrieve directory handle:', err);
    
    // Handle specific errors
    if (err instanceof DOMException) {
      if (err.name === 'VersionError') {
        console.log('Version error detected, attempting to delete and recreate database');
        await deleteDatabase();
      } else if (err.name === 'InvalidStateError') {
        console.log('Invalid state error detected, database may be corrupted');
        await deleteDatabase();
      }
    }
    
    return null;
  }
};

// Improve the deleteDatabase function with better error handling
const deleteDatabase = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    console.log('Deleting database: clipo-storage');
    
    // Make sure any existing connections are closed first
    const connections = indexedDB.databases ? indexedDB.databases() : Promise.resolve([]);
    connections.then(() => {
      const request = window.indexedDB.deleteDatabase('clipo-storage');
      
      request.onsuccess = () => {
        console.log('Database successfully deleted');
        resolve();
      };
      
      request.onerror = (event) => {
        console.error('Could not delete database:', event);
        reject(new Error('Failed to delete database'));
      };
      
      // Handle blocked events (when other tabs have the DB open)
      request.onblocked = () => {
        console.warn('Database deletion was blocked');
        // We'll resolve anyway, since we did our best
        resolve();
      };
    }).catch(err => {
      console.error('Error closing database connections:', err);
      // Try to delete anyway
      const request = window.indexedDB.deleteDatabase('clipo-storage');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete database'));
    });
  });
};

const STORAGE_KEY = {
  FOLDER_NAME: 'clipo-folder-name',
  LAST_SELECTION_TIME: 'clipo-last-selection-time',
  VIDEO_CACHE: 'clipo-video-cache'
};

export const useLocalVideos = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // Start with loading=false
  const [error, setError] = useState<string | null>(null);
  const [folderHandle, setFolderHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<PermissionState>('prompt');
  const [hasSelectedFolder, setHasSelectedFolder] = useState<boolean>(false);
  const [lastFolderName, setLastFolderName] = useState<string | null>(null);
  
  // Add new states to control loading timing
  const [loadStartTime, setLoadStartTime] = useState<number>(0);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [forceLoading, setForceLoading] = useState<boolean>(false);
  
  // Keep track of created blob URLs to revoke them when needed
  const blobUrlsRef = useRef<string[]>([]);
  
  // Add AbortController reference
  const abortControllerRef = useRef<AbortController | null>(null);

  // Function to revoke all blob URLs when component unmounts or when new ones are created
  const revokeBlobUrls = useCallback(() => {
    blobUrlsRef.current.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (err) {
        console.warn(`Could not revoke URL: ${url}`, err);
      }
    });
    blobUrlsRef.current = [];
  }, []);

  // Add function to cancel background tasks
  const cancelBackgroundTasks = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('Cancelling all background video processing...');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      revokeBlobUrls();
    };
  }, [revokeBlobUrls]);

  // Add to the beginning of your useLocalVideos hook
  useEffect(() => {
    const requestPersistentStorage = async () => {
      if (navigator.storage && navigator.storage.persist) {
        const isPersisted = await navigator.storage.persisted();
        console.log(`Storage persistence already ${isPersisted ? 'granted' : 'not granted'}`);
        
        if (!isPersisted) {
          // Request persistence
          const persistenceResult = await navigator.storage.persist();
          console.log(`Persistence request was ${persistenceResult ? 'granted' : 'denied'}`);
        }
      } else {
        console.log('Storage persistence not supported by this browser');
      }
    };
    
    requestPersistentStorage();
  }, []);

  // Modified quick video processing function to extract metadata more efficiently
  const quickProcessVideos = async (videoFiles: File[]): Promise<{ videos: Video[], blobUrls: string[] }> => {
    const newBlobUrls: string[] = [];
    
    // Process videos quickly using only essential metadata
    const processPromises = videoFiles.map(async (file, index) => {
      try {
        console.log(`Processing video ${index + 1}/${videoFiles.length}: ${file.name}`);
        
        // Create a blob URL for video playback - this is the only one we'll keep
        const videoUrl = URL.createObjectURL(file);
        newBlobUrls.push(videoUrl);
        
        // Format file name nicely for title
        const title = file.name
          .replace(/\.[^/.]+$/, "")  // Remove extension
          .replace(/_/g, ' ')        // Replace underscores with spaces
          .replace(/-/g, ' ')        // Replace hyphens with spaces
          .replace(/\s{2,}/g, ' ');  // Replace multiple spaces with single space
        
        // Create a searchable ID using the timestamp plus a sanitized filename
        const sanitizedName = file.name
          .replace(/\.[^/.]+$/, "")  // Remove extension
          .replace(/[^a-zA-Z0-9]/g, '_'); // Replace non-alphanumeric chars with underscore
        
        // Extract basic metadata (duration only) in a more efficient way
        // We'll do thumbnails later in the background
        let duration = 0;
        try {
          // Use a quick approach for metadata only
          const meta = await getQuickMetadata(file);
          duration = meta.duration;
        } catch (err) {
          console.warn(`Could not get duration for ${file.name}:`, err);
        }
        
        // Create a video object with default thumbnail but actual duration if available
        return {
          id: Date.now() + "-" + sanitizedName, // Numeric timestamp + sanitized filename for searchability
          title: title,
          url: videoUrl,
          thumbnailUrl: '/assets/default-thumbnail.jpg', // Default placeholder
          duration: duration, // May have actual duration already
          createdAt: new Date(file.lastModified).toISOString(),
          userId: 'local-user',
          size: file.size,
          format: file.type.split('/')[1] || 'mp4',
          isEnhanced: false
        };
      } catch (err) {
        console.error(`Error processing file ${file.name}:`, err);
        return null;
      }
    });
    
    // Use Promise.all for parallel processing but with a more efficient approach
    const processedVideos = Promise.all(processPromises)
      .then(results => results.filter((v): v is NonNullable<typeof v> => v !== null));
    
    return {
      videos: await processedVideos,
      blobUrls: newBlobUrls
    };
  };

// Simplified metadata extraction that's more lightweight
  const getQuickMetadata = async (file: File): Promise<{duration: number}> => {
    return new Promise((resolve, reject) => {
      // Check if we can use browser file metadata (in some browsers)
      if ('duration' in file && typeof file.duration === 'number') {
        return resolve({ duration: Math.floor(file.duration) });
      }
      
      // Fall back to video element
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      // Create URL with local tracking
      const videoUrl = URL.createObjectURL(file);
      
      // Set a short timeout
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(videoUrl);
        resolve({ duration: 0 });
      }, 2000);
      
      video.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        const duration = video.duration && !isNaN(video.duration) ? Math.floor(video.duration) : 0;
        URL.revokeObjectURL(videoUrl);
        resolve({ duration });
      };
      
      video.onerror = (e) => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(videoUrl);
        resolve({ duration: 0 });
      };
      
      video.src = videoUrl;
    });
  };

  // Enhanced metadata extraction with fast first-pass using metadata events
  const enhanceVideosWithMetadata = async (videoFiles: File[], initialVideos: Video[]) => {
    try {
      console.log('Starting background enhancement of videos...');
      
      // Use a smaller batch size for better performance
      const batchSize = 2; // Process just 2 videos at a time
      const totalVideos = videoFiles.length;
      
      for (let i = 0; i < totalVideos; i += batchSize) {
        // First prioritize getting accurate durations for all videos (faster operation)
        const durationBatch = videoFiles.slice(i, i + batchSize);
        const durationResults = await Promise.all(
          durationBatch.map(async (file, batchIndex) => {
            const index = i + batchIndex;
            try {
              // Quick duration extraction
              const duration = await getQuickMetadata(file);
              return {
                index,
                duration: duration.duration
              };
            } catch (err) {
              return { index, duration: 0 };
            }
          })
        );
        
        // Update videos with just durations first
        setVideos(currentVideos => {
          const updatedVideos = [...currentVideos];
          durationResults.forEach(result => {
            if (result.index < updatedVideos.length) {
              updatedVideos[result.index] = {
                ...updatedVideos[result.index],
                duration: result.duration
              };
            }
          });
          return updatedVideos;
        });
        
        // Pause between batches to let UI breathe
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // AFTER getting all durations, start generating thumbnails - one at a time
      // Update our thumbnail enhancement process
for (let i = 0; i < totalVideos; i++) {
  try {
    const file = videoFiles[i];
    console.log(`Generating thumbnail for video ${i + 1}/${totalVideos}: ${file.name}`);
    
    // Skip very small files that might be corrupted
    if (file.size < 1000) {
      console.warn(`Skipping thumbnail for small file (${file.size} bytes): ${file.name}`);
      continue;
    }
    
    // Generate a better thumbnail with black frame detection
    let thumbnailUrl: string;
    try {
      thumbnailUrl = await generateSimpleThumbnail(file);
      
      // Quick check if we got a data URL or the default
      if (thumbnailUrl.startsWith('data:')) {
        console.log(`Successfully generated thumbnail for ${file.name}`);
      } else {
        console.log(`Using default thumbnail for ${file.name}`);
      }
    } catch (err) {
      console.error(`Error generating thumbnail for ${file.name}:`, err);
      thumbnailUrl = '/assets/default-thumbnail.jpg';
    }
    
    // Update just this one video
    setVideos(currentVideos => {
      const updatedVideos = [...currentVideos];
      if (i < updatedVideos.length) {
        updatedVideos[i] = {
          ...updatedVideos[i],
          thumbnailUrl: thumbnailUrl,
          isEnhanced: true
        };
      }
      return updatedVideos;
    });
    
    // Important: wait between each thumbnail generation
    // Use a longer pause for larger files to avoid overloading the browser
    const pauseTime = file.size > 50000000 ? 400 : 200; // Longer pause for files > 50MB
    await new Promise(resolve => setTimeout(resolve, pauseTime));
  } catch (err) {
    console.error(`Error enhancing video ${i}:`, err);
  }
}
      
      console.log('Video enhancement completed');
      
    } catch (err) {
      console.error('Error in background enhancement process:', err);
    }
  };

// Improved thumbnail generation that now supports abort signals
const generateSimpleThumbnail = async (file: File, signal?: AbortSignal): Promise<string> => {
  return new Promise((resolve, reject) => {
    // If already aborted, return immediately
    if (signal && signal.aborted) {
      return resolve('/assets/default-thumbnail.jpg');
    }
    
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous'; // Add cross-origin attribute
    video.onerror = () => {
      clearTimeout(timeoutId);
      if (videoUrl) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {}
      }
      resolve('/assets/default-thumbnail.jpg');
    };
    
    // Create local URL to avoid conflicts
    let videoUrl: string;
    try {
      videoUrl = URL.createObjectURL(file);
    } catch (e) {
      // If creating URL fails, use default thumbnail
      resolve('/assets/default-thumbnail.jpg');
      return;
    }
    
    const timeoutId = setTimeout(() => {
      video.pause();
      try {
        URL.revokeObjectURL(videoUrl);
      } catch (e) {}
      resolve('/assets/default-thumbnail.jpg');
    }, 3000);
    
    // Setup abort handler if signal provided
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        video.pause();
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (e) {}
        resolve('/assets/default-thumbnail.jpg');
      });
    }
    
    // Function to check if an image is mostly black
    const isBlackFrame = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): boolean => {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let blackPixels = 0;
      const totalPixels = canvas.width * canvas.height;
      
      // Sample pixels (check every 20th pixel for efficiency)
      for (let i = 0; i < data.length; i += 80) { // 80 = 20 pixels * 4 (RGBA)
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // If average pixel value is very dark (less than 30 out of 255)
        if ((r + g + b) / 3 < 30) {
          blackPixels++;
        }
      }
      
      // If more than 80% of sampled pixels are black, consider it a black frame
      return (blackPixels / (totalPixels / 20)) > 0.8;
    };
    
    // Function to safely clean up resources
    const cleanupResources = () => {
      video.pause();
      video.removeAttribute('src');
      video.load(); // Force browser to release resources
      try {
        URL.revokeObjectURL(videoUrl);
      } catch (e) {}
    };
    
    // Try to get a good frame
    const generateThumbnail = () => {
      // Create a canvas with 9:16 aspect ratio for consistent thumbnails
      const canvas = document.createElement('canvas');
      const targetWidth = 135;
      const targetHeight = 240; // 9:16 aspect ratio
      
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        clearTimeout(timeoutId);
        cleanupResources();
        resolve('/assets/default-thumbnail.jpg');
        return;
      }
      
      // Fill with black background first
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate dimensions to maintain aspect ratio while filling the canvas (crop approach)
      const videoAspect = video.videoWidth / video.videoHeight;
      const canvasAspect = canvas.width / canvas.height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (videoAspect > canvasAspect) {
        // Video is wider than canvas - crop width, use full height
        drawHeight = canvas.height;
        drawWidth = video.videoWidth * (canvas.height / video.videoHeight);
        drawX = (canvas.width - drawWidth) / 2;
        drawY = 0;
      } else {
        // Video is taller than canvas - crop height, use full width
        drawWidth = canvas.width;
        drawHeight = video.videoHeight * (canvas.width / video.videoWidth);
        drawX = 0;
        drawY = (canvas.height - drawHeight) / 2;
      }
      
      // Draw the video frame, cropping to fit rather than stretching
      try {
        ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
      } catch (e) {
        // Handle any drawing errors
        clearTimeout(timeoutId);
        cleanupResources();
        resolve('/assets/default-thumbnail.jpg');
        return;
      }
      
      // Check if the frame is just black
      if (isBlackFrame(ctx, canvas)) {
        // Remove noisy black frame log
        
        // If it's a black frame and we have duration info, try to seek to a better position
        if (video.duration && video.duration > 2) {
          // Try to seek to 20% into the video
          video.currentTime = Math.min(video.duration * 0.2, video.duration - 1);
          
          // Set up an event handler for after seeking
          video.onseeked = () => {
            try {
              // Fill background
              ctx.fillStyle = '#000000';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              
              // Calculate dimensions to maintain aspect ratio (same cropping logic)
              const videoAspect = video.videoWidth / video.videoHeight;
              const canvasAspect = canvas.width / canvas.height;
              let drawWidth, drawHeight, drawX, drawY;
              
              if (videoAspect > canvasAspect) {
                // Video is wider than canvas - crop width, use full height
                drawHeight = canvas.height;
                drawWidth = video.videoWidth * (canvas.height / video.videoHeight);
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              } else {
                // Video is taller than canvas - crop height, use full width
                drawWidth = canvas.width;
                drawHeight = video.videoHeight * (canvas.width / video.videoWidth);
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              }
              
              // Redraw with the new frame using proper cropping
              ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
              
              // Check if still black
              if (isBlackFrame(ctx, canvas)) {
                // Remove noisy seeking log
                
                // Try one more position (middle of video)
                video.currentTime = Math.min(video.duration * 0.5, video.duration - 0.5);
                
                video.onseeked = () => {
                  try {
                    // Fill background
                    ctx.fillStyle = '#000000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    
                    // Calculate dimensions to maintain aspect ratio (same cropping logic)
                    const videoAspect = video.videoWidth / video.videoHeight;
                    const canvasAspect = canvas.width / canvas.height;
                    let drawWidth, drawHeight, drawX, drawY;
                    
                    if (videoAspect > canvasAspect) {
                      // Video is wider than canvas - crop width, use full height
                      drawHeight = canvas.height;
                      drawWidth = video.videoWidth * (canvas.height / video.videoHeight);
                      drawX = (canvas.width - drawWidth) / 2;
                      drawY = 0;
                    } else {
                      // Video is taller than canvas - crop height, use full width
                      drawWidth = canvas.width;
                      drawHeight = video.videoHeight * (canvas.width / video.videoWidth);
                      drawX = 0;
                      drawY = (canvas.height - drawHeight) / 2;
                    }
                    
                    // Draw with proper cropping
                    ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
                  } catch (e) {
                    // Handle drawing errors
                    clearTimeout(timeoutId);
                    cleanupResources();
                    resolve('/assets/default-thumbnail.jpg');
                  }
                };
                
                // Timeout for second seek
                const seekTimeout = setTimeout(() => {
                  try {
                    const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
                    clearTimeout(timeoutId);
                    cleanupResources();
                    resolve(thumbnailUrl);
                  } catch (e) {
                    clearTimeout(timeoutId);
                    cleanupResources();
                    resolve('/assets/default-thumbnail.jpg');
                  }
                }, 1000);
                
                video.onerror = () => {
                  clearTimeout(seekTimeout);
                  clearTimeout(timeoutId);
                  cleanupResources();
                  resolve('/assets/default-thumbnail.jpg');
                };
                
                return;
              }
              
              // If not black anymore, use this frame
              try {
                const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
                clearTimeout(timeoutId);
                cleanupResources();
                resolve(thumbnailUrl);
              } catch (e) {
                clearTimeout(timeoutId);
                cleanupResources();
                resolve('/assets/default-thumbnail.jpg');
              }
            } catch (err) {
              console.error('Error capturing frame after seeking:', err);
              clearTimeout(timeoutId);
              cleanupResources();
              resolve('/assets/default-thumbnail.jpg');
            }
          };
          
          // Timeout for first seek
          const seekTimeout = setTimeout(() => {
            try {
              const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
              clearTimeout(timeoutId);
              cleanupResources();
              resolve(thumbnailUrl);
            } catch (e) {
              clearTimeout(timeoutId);
              cleanupResources();
              resolve('/assets/default-thumbnail.jpg');
            }
          }, 1000);
          
          video.onerror = () => {
            clearTimeout(seekTimeout);
            clearTimeout(timeoutId);
            cleanupResources();
            resolve('/assets/default-thumbnail.jpg');
          };
          
          return; // Wait for seek to complete
        }
      }
      
      // If we got here, either the frame is not black, or we can't seek
      // Use the current frame
      try {
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
        clearTimeout(timeoutId);
        cleanupResources();
        resolve(thumbnailUrl);
      } catch (e) {
        clearTimeout(timeoutId);
        cleanupResources();
        resolve('/assets/default-thumbnail.jpg');
      }
    };
    
    video.onloadeddata = generateThumbnail;
    
    // Set video source
    try {
      video.src = videoUrl;
    } catch (e) {
      cleanupResources();
      resolve('/assets/default-thumbnail.jpg');
    }
  });
};

  // Fast metadata extraction that just gets basic video properties
  const getBasicMetadata = async (file: File): Promise<{duration: number, width: number, height: number}> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const videoUrl = URL.createObjectURL(file);
      // Track created URL separately from those that should be automatically revoked
      const localUrlsToRevoke: string[] = [];
      localUrlsToRevoke.push(videoUrl);
      
      const timeoutId = setTimeout(() => {
        console.warn(`Metadata extraction timed out for ${file.name}`);
        // Clean up URLs created in this function only
        localUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        resolve({duration: 0, width: 0, height: 0});
      }, 3000);
      
      video.onloadedmetadata = () => {
        try {
          clearTimeout(timeoutId);
          
          const result = {
            duration: video.duration && !isNaN(video.duration) ? Math.floor(video.duration) : 0,
            width: video.videoWidth || 0,
            height: video.videoHeight || 0
          };
          
          // Clean up URLs created in this function only
          localUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
          resolve(result);
        } catch (err) {
          console.error(`Error extracting metadata for ${file.name}:`, err);
          // Clean up URLs created in this function only
          localUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
          resolve({duration: 0, width: 0, height: 0});
        }
      };
      
      video.onerror = () => {
        console.error(`Error loading video for metadata: ${file.name}`);
        clearTimeout(timeoutId);
        // Clean up URLs created in this function only
        localUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        resolve({duration: 0, width: 0, height: 0});
      };
      
      // Set video source to the file
      video.src = videoUrl;
    });
  };

  // Optimized thumbnail generation that tries different methods
  const generateOptimizedThumbnail = async (
    file: File, 
    knownMetadata?: {width: number, height: number}
  ): Promise<string> => {
    // First, try to use the faster first-frame method (doesn't require seeking)
    try {
      const quickThumb = await generateQuickThumbnail(file, knownMetadata);
      if (quickThumb && quickThumb !== '/assets/default-thumbnail.jpg') {
        return quickThumb;
      }
    } catch (err) {
      console.warn(`Quick thumbnail generation failed, falling back to slower method: ${err}`);
    }
    
    // If that fails, try the more thorough but slower method with seeking
    return generateSimpleThumbnail(file);
  };

  // Fast thumbnail generator that grabs the first frame without seeking
  const generateQuickThumbnail = async (
    file: File, 
    knownMetadata?: {width: number, height: number}
  ): Promise<string> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.playsInline = true;
      video.muted = true;
      
      // Very short timeout for quick first-frame capture
      const timeoutId = setTimeout(() => {
        console.warn(`Quick thumbnail timed out for ${file.name}`);
        video.pause();
        URL.revokeObjectURL(video.src);
        resolve('/assets/default-thumbnail.jpg');
      }, 3000);
      
      // When video starts playing, capture the first frame
      video.oncanplay = () => {
        try {
          // Pause video to stop further loading
          video.pause();
          
          // Create canvas for capturing the frame with a fixed 9:16 aspect ratio
          const canvas = document.createElement('canvas');
          const targetWidth = 135;
          const targetHeight = 240; // 9:16 aspect ratio
          
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          
          const ctx = canvas.getContext('2d');
          if (ctx) {
            // Fill with black background
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Calculate dimensions to maintain aspect ratio while filling the canvas (crop approach)
            const videoAspect = video.videoWidth / video.videoHeight;
            const canvasAspect = canvas.width / canvas.height;
            
            let drawWidth, drawHeight, drawX, drawY;
            
            if (videoAspect > canvasAspect) {
              // Video is wider than canvas - crop width, use full height
              drawHeight = canvas.height;
              drawWidth = video.videoWidth * (canvas.height / video.videoHeight);
              drawX = (canvas.width - drawWidth) / 2;
              drawY = 0;
            } else {
              // Video is taller than canvas - crop height, use full width
              drawWidth = canvas.width;
              drawHeight = video.videoHeight * (canvas.width / video.videoWidth);
              drawX = 0;
              drawY = (canvas.height - drawHeight) / 2;
            }
            
            // Draw the video frame, cropping to fit rather than stretching
            ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
            
            // Use a lower quality for speed
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
            clearTimeout(timeoutId);
            URL.revokeObjectURL(video.src);
            resolve(thumbnailUrl);
          } else {
            console.error('Failed to get canvas context');
            clearTimeout(timeoutId);
            URL.revokeObjectURL(video.src);
            resolve('/assets/default-thumbnail.jpg');
          }
        } catch (err) {
          console.error(`Error capturing first frame for ${file.name}:`, err);
          clearTimeout(timeoutId);
          URL.revokeObjectURL(video.src);
          resolve('/assets/default-thumbnail.jpg');
        }
      };
      
      video.onerror = () => {
        console.error(`Error loading video for quick thumbnail: ${file.name}`);
        clearTimeout(timeoutId);
        URL.revokeObjectURL(video.src);
        resolve('/assets/default-thumbnail.jpg');
      };
      
      // Set video source and start playing to trigger oncanplay
      video.src = URL.createObjectURL(file);
      
      // Play the video to generate the first frame
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          console.warn('Auto-play was prevented, forcing frame capture');
          // Try to capture frame even if play fails
          if (video.oncanplay) {
            video.oncanplay(new Event('canplay'));
          }
        });
      }
    });
  };

  // Cache duration for a video
  const saveDurationToCache = (videoId: string, duration: number) => {
    try {
      const durationKey = `clipo-duration-${videoId}`;
      sessionStorage.setItem(durationKey, duration.toString());
    } catch (err) {
      console.warn('Failed to save duration to cache:', err);
    }
  };
  
  // Get duration from cache
  const getDurationFromCache = (videoId: string): number | null => {
    try {
      const durationKey = `clipo-duration-${videoId}`;
      const cached = sessionStorage.getItem(durationKey);
      return cached ? parseInt(cached, 10) : null;
    } catch (err) {
      console.warn('Failed to get duration from cache:', err);
      return null;
    }
  };
  
  // Save thumbnail to dedicated cache
  const saveThumbnailToCache = (videoId: string, thumbnailUrl: string) => {
    try {
      // Only cache if it's not the default thumbnail
      if (thumbnailUrl === '/assets/default-thumbnail.jpg') return;
      
      // Add to thumbnails cache
      const thumbKey = `clipo-thumbnail-${videoId}`;
      
      // Only cache data URLs that aren't too large (sometimes they can be over 1MB)
      if (thumbnailUrl.startsWith('data:') && thumbnailUrl.length > 200000) {
        console.log(`Thumbnail for ${videoId} too large (${Math.round(thumbnailUrl.length/1024)}KB), not caching`);
        return;
      }
      
      sessionStorage.setItem(thumbKey, thumbnailUrl);
    } catch (err) {
      console.warn('Failed to save thumbnail to cache:', err);
    }
  };
  
  // Get thumbnail from cache
  const getThumbnailFromCache = (videoId: string): string | null => {
    try {
      const thumbKey = `clipo-thumbnail-${videoId}`;
      return sessionStorage.getItem(thumbKey);
    } catch (err) {
      console.warn('Failed to get thumbnail from cache:', err);
      return null;
    }
  };
  
  // Clear all caches when going to home page
  const clearAllCaches = () => {
    try {
      // Get all keys in session storage
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('clipo-thumbnail-') || key.startsWith('clipo-duration-') || key === STORAGE_KEY.VIDEO_CACHE)) {
          keys.push(key);
        }
      }
      
      // Remove all cache entries
      keys.forEach(key => sessionStorage.removeItem(key));
      console.log(`Cleared ${keys.length} cache entries`);
    } catch (err) {
      console.error('Error clearing caches:', err);
    }
  };

  // Store videos in cache for retrieval when returning from checkout
  const saveVideosToCache = (videos: Video[]) => {
    try {
      // Cache each video's thumbnail separately
      videos.forEach(video => {
        if (video.id && video.thumbnailUrl) {
          saveThumbnailToCache(video.id, video.thumbnailUrl);
        }
        
        if (video.id && video.duration) {
          saveDurationToCache(video.id, video.duration);
        }
      });
      
      // Also store basic video data without the thumbnails
      const cacheableVideos = videos.map(video => ({
        id: video.id,
        title: video.title,
        url: video.url,
        // Don't store the full thumbnails in the main cache
        thumbnailUrl: '/assets/default-thumbnail.jpg',
        duration: video.duration,
        createdAt: video.createdAt,
        userId: video.userId,
        size: video.size,
        format: video.format
      }));
      
      sessionStorage.setItem(STORAGE_KEY.VIDEO_CACHE, JSON.stringify(cacheableVideos));
      // Reduce log frequency - only log once
      if (!window._lastCacheLog || Date.now() - window._lastCacheLog > 5000) {
        console.log(`Cached ${videos.length} videos to session storage`);
        window._lastCacheLog = Date.now();
      }
      return true;
    } catch (err) {
      console.error('Failed to save videos to cache:', err);
      return false;
    }
  };
  
  // Get videos from cache
  const getVideosFromCache = (): Partial<Video>[] | null => {
    try {
      const cachedVideos = sessionStorage.getItem(STORAGE_KEY.VIDEO_CACHE);
      if (!cachedVideos) return null;
      
      const parsedVideos = JSON.parse(cachedVideos);
      return parsedVideos;
    } catch (err) {
      console.error('Failed to get videos from cache:', err);
      return null;
    }
  };
  
  // After enhancement, save to cache - now with abort support
  const backgroundEnhanceVideos = async (files: File[], initialVideos: Video[]) => {
    // Create a new AbortController for this process
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Cancel any existing tasks
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    const enhancedVideos = [...initialVideos];
    
    try {
      // First pass - get durations for all videos
      for (let i = 0; i < files.length; i++) {
        // Check if processing has been aborted
        if (signal.aborted) {
          console.log('Video enhancement aborted during duration extraction');
          return;
        }
        
        try {
          const duration = await getSimpleDuration(files[i], signal);
          
          // Check again if aborted before updating state
          if (signal.aborted) return;
          
          // Update only this video's duration
          enhancedVideos[i] = {...enhancedVideos[i], duration};
          setVideos(currentVideos => 
            currentVideos.map((v, idx) => 
              idx === i ? {...v, duration} : v
            )
          );
          
          // Cache the duration
          if (enhancedVideos[i].id) {
            saveDurationToCache(enhancedVideos[i].id, duration);
          }
          
          // Pause briefly between each video
          await new Promise(r => setTimeout(r, 50));
        } catch (err) {
          if (signal.aborted) return;
          console.warn(`Could not get duration for video ${i+1}:`, err);
        }
      }
      
      // Second pass - thumbnails (one at a time)
      for (let i = 0; i < files.length; i++) {
        // Check if processing has been aborted
        if (signal.aborted) {
          console.log('Video enhancement aborted during thumbnail generation');
          return;
        }
        
        try {
          const thumbnailUrl = await generateSimpleThumbnail(files[i], signal);
          
          // Check again if aborted before updating state
          if (signal.aborted) return;
          
          // Update video in state and enhancedVideos
          enhancedVideos[i] = {...enhancedVideos[i], thumbnailUrl, isEnhanced: true};
          setVideos(currentVideos => 
            currentVideos.map((v, idx) => 
              idx === i ? {...v, thumbnailUrl, isEnhanced: true} : v
            )
          );
          
          // Cache the thumbnail
          if (enhancedVideos[i].id) {
            saveThumbnailToCache(enhancedVideos[i].id, thumbnailUrl);
          }
          
          // Longer pause between thumbnail generations
          await new Promise(r => setTimeout(r, 200));
        } catch (err) {
          if (signal.aborted) return;
          console.warn(`Could not generate thumbnail for video ${i+1}:`, err);
        }
      }
      
      // Only save to cache if not aborted
      if (!signal.aborted) {
        // Save all enhanced videos to cache
        saveVideosToCache(enhancedVideos);
        console.log('Video enhancement completed successfully');
      }
    } catch (err) {
      if (signal.aborted) {
        console.log('Video enhancement process was aborted');
      } else {
        console.error('Error in video enhancement process:', err);
      }
    }
  };
  
  // Function to restore videos from cache when returning from checkout
  const restoreFromCache = useCallback(async () => {
    try {
      console.log('Attempting to restore videos from cache');
      const cachedVideos = getVideosFromCache();
      
      if (cachedVideos && cachedVideos.length > 0) {
        console.log(`Found ${cachedVideos.length} cached videos, restoring from cache`);
        
        // Restore thumbnails and durations from dedicated cache
        const restoredVideos = cachedVideos.map(video => {
          if (!video.id) return video;
          
          // Get thumbnail from dedicated cache
          const cachedThumbnail = getThumbnailFromCache(video.id);
          
          // Get duration from dedicated cache
          const cachedDuration = getDurationFromCache(video.id);
          
          return {
            ...video,
            thumbnailUrl: cachedThumbnail || video.thumbnailUrl,
            duration: cachedDuration !== null ? cachedDuration : video.duration
          };
        });
        
        setVideos(restoredVideos as Video[]);
        setHasSelectedFolder(true);
        setLoading(false);
        return true;
      } else {
        console.log('No cached videos found to restore');
        setLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Error restoring from cache:', err);
      setLoading(false);
      return false;
    }
  }, []);

  // Save videos to cache whenever videos state changes (if we have videos)
  useEffect(() => {
    if (videos.length > 0 && hasSelectedFolder) {
      // Only log once when caching starts instead of every time
      if (window._lastLoggedVideosCount !== videos.length) {
        console.log(`Auto-saving ${videos.length} videos to cache for persistence`);
        window._lastLoggedVideosCount = videos.length;
      }
      saveVideosToCache(videos);
    }
  }, [videos, hasSelectedFolder]);

  // Add effect to handle navigation from checkout page
  useEffect(() => {
    const handleReturnFromCheckout = async () => {
      const fromCheckout = sessionStorage.getItem('came-from-checkout') === 'true';
      if (fromCheckout) {
        console.log('Detected return from checkout page');
        sessionStorage.removeItem('came-from-checkout'); // Clear flag
        
        setLoading(true);
        await restoreFromCache();
      }
    };
    
    handleReturnFromCheckout();
  }, [restoreFromCache]);

  // Add an unload effect to clear cache when navigating to home page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear if navigating away from video selection to home
      // (we want to keep the cache when going to checkout)
      if (window.location.pathname === '/video-selection') {
        const isGoingToCheckout = sessionStorage.getItem('going-to-checkout') === 'true';
        if (!isGoingToCheckout) {
          clearAllCaches();
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  // Update the loadVideosFromHandle function to save videos to cache
  const loadVideosFromHandle = useCallback(async (dirHandle: FileSystemDirectoryHandle): Promise<boolean> => {
    try {
      // Start tracking load time
      const startTime = Date.now();
      setLoadStartTime(startTime);
      setDataLoaded(false);
      setForceLoading(true);
      setLoading(true);
      setError(null); // Clear any previous errors
      
      // Save folder name and details
      const folderName = dirHandle.name;
      localStorage.setItem(STORAGE_KEY.FOLDER_NAME, folderName);
      localStorage.setItem(STORAGE_KEY.LAST_SELECTION_TIME, Date.now().toString());
      setLastFolderName(folderName);
      
      // Save the directory handle to IndexedDB for persistence
      await storeDirectoryHandle(dirHandle);
      
      console.log(`Loading videos from folder: ${folderName}`);
      
      // Get list of video files
      const videoFiles: File[] = [];
      const validExtensions = ['.mp4', '.mov', '.webm'];
      
      try {
        // First collect all files with their metadata
        const fileEntries: { file: File, lastModified: number, name: string }[] = [];
        for await (const entry of dirHandle.values()) {
          if (entry.kind === 'file') {
            const fileName = entry.name.toLowerCase();
            if (validExtensions.some(ext => fileName.endsWith(ext))) {
              try {
                const fileHandle = entry as FileSystemFileHandle;
                const file = await fileHandle.getFile();
                fileEntries.push({ 
                  file, 
                  lastModified: file.lastModified,
                  name: entry.name 
                });
              } catch (fileErr) {
                console.warn(`Skipping file ${entry.name}:`, fileErr);
              }
            }
          }
        }
        
        // Sort entries by lastModified in descending order
        fileEntries.sort((a, b) => {
          // First try to sort by lastModified
          const timeDiff = b.lastModified - a.lastModified;
          if (timeDiff !== 0) return timeDiff;
          
          // If lastModified is the same, sort by name to ensure consistent order
          return a.name.localeCompare(b.name);
        });
        
        // Extract just the files in the sorted order
        videoFiles.push(...fileEntries.map(entry => entry.file));
        
      } catch (dirErr) {
        console.error('Error reading directory:', dirErr);
        setError('Could not read the folder contents');
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return false;
      }
      
      if (videoFiles.length === 0) {
        setError('No video files found in the selected folder');
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return false;
      }
      
      console.log(`Found ${videoFiles.length} video files`);
      
      // Revoke any existing blob URLs before creating new ones
      revokeBlobUrls();
      
      // Process videos quickly - just create URLs and basic objects
      const newBlobUrls: string[] = [];
      const quickProcessedVideos = videoFiles.map((file, index) => {
        // Create a blob URL for video playback
        const videoUrl = URL.createObjectURL(file);
        newBlobUrls.push(videoUrl);
        
        // Format file name
        const title = file.name
          .replace(/\.[^/.]+$/, "")
          .replace(/_/g, ' ')
          .replace(/-/g, ' ');
        
        // Create a searchable ID using the timestamp plus a sanitized filename
        const sanitizedName = file.name
          .replace(/\.[^/.]+$/, "")  // Remove extension
          .replace(/[^a-zA-Z0-9]/g, '_'); // Replace non-alphanumeric chars with underscore
        
        // Create basic video object
        return {
          id: Date.now() + "-" + sanitizedName, // Numeric timestamp + sanitized filename for searchability
          title,
          url: videoUrl,
          thumbnailUrl: '/assets/default-thumbnail.jpg',
          duration: 0,
          createdAt: new Date(file.lastModified).toISOString(),
          userId: 'local-user',
          isEnhanced: false
        };
      });
      
      // Update state and store blob URLs
      blobUrlsRef.current = newBlobUrls;
      setVideos(quickProcessedVideos);
      
      // Mark that data is loaded, but keep the loading state
      setDataLoaded(true);
      
      // Check if we've been loading for at least 3 seconds
      checkMinimumLoadingTime(startTime);
      
      // Do enhancement in the background with a delay, using the sorted files
      setTimeout(() => {
        backgroundEnhanceVideos(videoFiles, quickProcessedVideos);
      }, 1000);
      
      return true;
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos from the selected folder');
      setDataLoaded(true);
      checkMinimumLoadingTime(loadStartTime);
      return false;
    }
  }, [revokeBlobUrls, backgroundEnhanceVideos, loadStartTime]);
  
  // Ultra-simple duration getter with abort support
  const getSimpleDuration = async (file: File, signal?: AbortSignal): Promise<number> => {
    return new Promise((resolve) => {
      // If already aborted, return immediately
      if (signal && signal.aborted) {
        return resolve(0);
      }
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const timeoutId = setTimeout(() => resolve(0), 2000);
      
      // Setup abort handler if signal provided
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          video.src = '';
          resolve(0);
        });
      }
      
      video.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        const duration = video.duration && !isNaN(video.duration) ? Math.floor(video.duration) : 0;
        video.src = '';
        resolve(duration);
      };
      
      video.onerror = () => {
        clearTimeout(timeoutId);
        resolve(0);
      };
      
      video.src = URL.createObjectURL(file);
      // This URL will be automatically garbage collected
    });
  };

  // Add a helper function to check if minimum loading time has elapsed
  const checkMinimumLoadingTime = useCallback((startTime: number) => {
    console.log("Checking minimum loading time, startTime:", startTime);
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;
    const minimumLoadTime = 3000; // 3 seconds minimum
    
    if (elapsedTime >= minimumLoadTime) {
      // We've already been loading for at least 3 seconds
      console.log("Minimum loading time already elapsed, setting loading=false");
      setLoading(false);
      setForceLoading(false);
    } else {
      // We need to continue loading for a bit longer
      const remainingTime = minimumLoadTime - elapsedTime;
      console.log(`Data loaded, but keeping loading screen for ${remainingTime}ms more`);
      
      // Use a ref to avoid setting state multiple times
      const timerRef = setTimeout(() => {
        console.log("Minimum loading time complete, now setting loading=false");
        setLoading(false);
        setForceLoading(false);
      }, remainingTime);
      
      // Store the timer ref for cleanup
      return () => clearTimeout(timerRef);
    }
  }, []);
  
  // Add a safety fallback to ensure loading state doesn't get stuck
  useEffect(() => {
    // If loading is true for more than 20 seconds, something has gone wrong
    // This is a safety net to prevent infinite loading
    if (loading || forceLoading) {
      console.log("Loading safety timer started - will force end loading in 20 seconds if still loading");
      const safetyTimer = setTimeout(() => {
        if (loading || forceLoading) {
          console.log("⚠️ SAFETY TIMEOUT: Loading state was active for 20 seconds, forcing it to false");
          setLoading(false);
          setForceLoading(false);
          setError(prev => prev || "Loading took too long. Please try again.");
        }
      }, 20000); // 20 second safety timeout
      
      return () => clearTimeout(safetyTimer);
    }
  }, [loading, forceLoading]);

  // Simplified selectClipoFolder function that focuses on robustness
  const selectClipoFolder = useCallback(async (useStoredOnly: boolean = false): Promise<{success: boolean}> => {
    try {
      // Start tracking load time
      const startTime = Date.now();
      setLoadStartTime(startTime);
      setDataLoaded(false);
      setForceLoading(true);
      setLoading(true);
      
      // Check for the special admin button force flag
      const forceDirectoryPicker = sessionStorage.getItem('force-directory-picker') === 'true';
      if (forceDirectoryPicker) {
        console.log('Force directory picker flag detected - directly showing file picker');
        // Skip all folder handle checks and go straight to directory picker
        return await showDirectoryPicker();
      }
      
      console.log('Starting folder selection process, useStoredOnly:', useStoredOnly);
      
      // If we don't have a folder handle in state, try to get it from IndexedDB
      if (!folderHandle) {
        try {
          const storedHandle = await retrieveDirectoryHandle();
          if (storedHandle) {
            console.log('Retrieved directory handle from storage:', storedHandle.name);
            setFolderHandle(storedHandle);
            
            // Now we have the folder handle, try to use it
            try {
              // Check permission status
              const permission = await storedHandle.requestPermission({ mode: 'read' });
              setPermissionStatus(permission);
              
              if (permission === 'granted') {
                console.log('Permission granted for stored folder handle');
                
                // We have permission - load videos from stored handle
                const success = await loadVideosFromHandle(storedHandle);
                if (success) {
                  setHasSelectedFolder(true);
                  setDataLoaded(true);
                  checkMinimumLoadingTime(startTime);
                  return { success: true };
                }
              } else {
                console.log('Permission denied for stored folder handle');
              }
            } catch (err) {
              console.warn('Could not use stored folder handle:', err);
            }
          }
        } catch (err) {
          console.error('Error retrieving handle from IndexedDB:', err);
          // Proceed without the stored handle
        }
      } else {
        // We already have a folder handle, try to use it
        try {
          console.log('Using existing folder handle');
          
          // Check permission status
          const permission = await folderHandle.requestPermission({ mode: 'read' });
          setPermissionStatus(permission);
          
          if (permission === 'granted') {
            console.log('Permission granted for stored folder handle');
            
            // We have permission - load videos from stored handle
            const success = await loadVideosFromHandle(folderHandle);
            if (success) {
              setHasSelectedFolder(true);
              setDataLoaded(true);
              checkMinimumLoadingTime(startTime);
              return { success: true };
            }
          }
        } catch (err) {
          console.warn('Could not use stored folder handle:', err);
        }
      }
      
      // If we should only try using stored directory and it failed, or we want to show picker
      if (useStoredOnly) {
        console.log('Using stored folder only - failed to load');
        setDataLoaded(true);
        checkMinimumLoadingTime(startTime);
        return { success: false };
      }
      
      // Show file picker
      return await showDirectoryPicker();
    } catch (err) {
      console.error('Error in selectClipoFolder:', err);
      setDataLoaded(true);
      checkMinimumLoadingTime(loadStartTime);
      return { success: false };
    }
  }, [folderHandle, loadVideosFromHandle, loadStartTime, checkMinimumLoadingTime]);

  // Create a separate function for showing the directory picker
  // This helps separate the logic for reusing stored handles vs showing the picker
  const showDirectoryPicker = useCallback(async (): Promise<{success: boolean}> => {
    try {
      console.log('showDirectoryPicker called, checking browser support');
      
      if (!window.showDirectoryPicker || typeof window.showDirectoryPicker !== 'function') {
        console.error('File System Access API not supported in this browser');
        setError('This feature requires Chrome or Edge browser');
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return { success: false };
      }

      // Add a consistent ID for better permission caching
      const pickerOptions = {
        id: 'clipoVideoPicker', 
        mode: 'read' as const,
        startIn: 'desktop' as const
      };
      
      // Open the picker with these options
      console.log('Opening directory picker');
      const dirHandle = await window.showDirectoryPicker(pickerOptions);
      console.log('User selected directory:', dirHandle.name);
      
      // Request persistent permission
      console.log('Requesting permission for selected directory');
      const permissionResult = await dirHandle.requestPermission({ mode: 'read' });
      setPermissionStatus(permissionResult);
      
      if (permissionResult !== 'granted') {
        console.log('Permission not granted for selected directory:', permissionResult);
        setError('Permission not granted for the selected folder');
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return { success: false };
      }
      
      // Store the handle for future use
      setFolderHandle(dirHandle);
      
      // Also store it in IndexedDB for persistence
      await storeDirectoryHandle(dirHandle);
      
      // Load videos from this folder
      console.log('Loading videos from selected directory');
      const success = await loadVideosFromHandle(dirHandle);
      if (success) {
        setHasSelectedFolder(true);
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return { success: true };
      } else {
        console.log('Failed to load videos from selected directory');
        setDataLoaded(true);
        checkMinimumLoadingTime(loadStartTime);
        return { success: false };
      }
    } catch (err: any) {
      console.error('Directory picker error:', err);
      
      if (err.name === 'AbortError') {
        console.log('User cancelled directory selection');
        setError('Folder selection was cancelled');
      } else {
        console.error('Error showing directory picker:', err);
        setError(err.message || 'Failed to access the folder');
      }
      
      setDataLoaded(true);
      checkMinimumLoadingTime(loadStartTime);
      return { success: false };
    }
  }, [loadVideosFromHandle, loadStartTime, checkMinimumLoadingTime]);

  // Try to auto-restore on initial load with better handling of recursion issues
  const hasInitializedRef = useRef(false);
  const isFolderSelectingRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    
    // Prevent multiple initialization attempts
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    
    const initializeVideos = async () => {
      if (!isMounted) return; // Don't proceed if component unmounted
      
      // Check if we have a folder selected from a previous session
      const savedFolderName = localStorage.getItem(STORAGE_KEY.FOLDER_NAME);
      const lastSelectionTime = localStorage.getItem(STORAGE_KEY.LAST_SELECTION_TIME);
      
      if (savedFolderName && lastSelectionTime) {
        try {
          const lastDate = new Date(parseInt(lastSelectionTime, 10));
          const today = new Date();
          
          // Only try auto-restoring if the folder was selected today
          if (lastDate.toDateString() === today.toDateString()) {
            // Prevent recursive calls that could cause infinite loops
            if (isFolderSelectingRef.current) {
              if (isMounted) setLoading(false);
              return;
            }
            
            // Set the flag to indicate we're in the process of folder selection
            isFolderSelectingRef.current = true;
            
            try {
              await selectClipoFolder(false);
            } catch (err) {
              console.error("Error during auto-restore:", err);
            } finally {
              // Clear the selection flag when done
              isFolderSelectingRef.current = false;
            }
          }
        } catch (err) {
          console.error("Error parsing date or auto-restoring folder:", err);
        }
      }
      
      // Ensure loading state is turned off
      if (isMounted) setLoading(false);
    };
    
    initializeVideos();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectClipoFolder]);
  
  // Add an effect to handle minimum loading time
  useEffect(() => {
    // Check if we need to end force loading
    if (forceLoading && dataLoaded) {
      const currentTime = Date.now();
      const elapsedTime = currentTime - loadStartTime;
      const minimumLoadTime = 3000; // 3 seconds minimum
      
      if (elapsedTime >= minimumLoadTime) {
        setLoading(false);
        setForceLoading(false);
      }
    }
  }, [forceLoading, dataLoaded, loadStartTime]);

  // Add a method to get the actual video file objects for the selected IDs
  const getVideoFiles = useCallback(async (videoIds: string[]): Promise<{title: string, file: File}[]> => {
    if (!folderHandle) {
      throw new Error('No folder selected');
    }
    
    try {
      // Request permission again to ensure we still have access
      const permission = await folderHandle.requestPermission({ mode: 'read' });
      if (permission !== 'granted') {
        throw new Error('Permission denied to access folder');
      }
      
      // Store original videoIds for logging
      const originalVideoIds = [...videoIds];
      console.log('Original requested videoIds:', originalVideoIds);
      
      const result: {title: string, file: File}[] = [];
      const fileEntries: { file: File, name: string, id?: string, confidence?: number }[] = [];
      
      // First, iterate through the folder to get ALL video files regardless of date
      // This ensures we can find older videos for the resend functionality
      console.log('Scanning folder for ALL videos regardless of creation date');
      for await (const entry of folderHandle.values()) {
        if (entry.kind === 'file') {
          try {
            const fileHandle = entry as FileSystemFileHandle;
            const file = await fileHandle.getFile();
            
            // Check if the file is a video
            if (file.type.startsWith('video/') || file.name.match(/\.(mp4|avi|mov|wmv|flv|mkv)$/i)) {
              fileEntries.push({ 
                file, 
                name: entry.name
              });
            }
          } catch (fileErr) {
            console.warn(`Skipping file ${entry.name}:`, fileErr);
          }
        }
      }
      
      console.log(`Found ${fileEntries.length} total video files in folder:`, fileEntries.map(e => e.name));
      console.log('Looking for video IDs:', videoIds);
      
      // Improved matching algorithm with confidence scoring
      for (let i = 0; i < videoIds.length; i++) {
        const videoId = videoIds[i];
        let bestMatch: { entry: typeof fileEntries[0], confidence: number } | null = null;
        
        // Extract filename from ID if it's in timestamp-filename format
        let filenameFromId = '';
        if (videoId.includes('-')) {
          // Get everything after the first hyphen as the potential filename
          filenameFromId = videoId.split('-').slice(1).join('-');
        }
        
        console.log(`Looking for match for videoId: ${videoId}, extracted filename: ${filenameFromId}`);
        
        // First pass: Look for exact matches with highest confidence
        for (let j = 0; j < fileEntries.length; j++) {
          const entry = fileEntries[j];
          if (entry.id) continue; // Skip already matched entries
          
          const entryNameWithoutExt = entry.name.replace(/\.[^/.]+$/, ""); // Remove extension
          
          // Calculate match confidence based on different matching strategies
          let confidence = 0;
          
          // 1. Exact match with the full filename (highest confidence)
          if (entryNameWithoutExt === filenameFromId) {
            confidence = 100;
          }
          // 2. Full videoId contains the entire filename
          else if (videoId.includes(entryNameWithoutExt)) {
            confidence = 90;
          }
          // 3. Filename contains the extracted part after hyphen
          else if (entryNameWithoutExt.includes(filenameFromId)) {
            // Longer matches are better (avoid matching just a small part)
            confidence = 80 * (filenameFromId.length / entryNameWithoutExt.length);
          }
          // 4. Case-insensitive match
          else if (entryNameWithoutExt.toLowerCase() === filenameFromId.toLowerCase()) {
            confidence = 70;
          }
          // 5. Replace underscores with spaces/hyphens for comparison
          else if (entryNameWithoutExt.replace(/_/g, ' ') === filenameFromId.replace(/_/g, ' ') ||
                   entryNameWithoutExt.replace(/_/g, '-') === filenameFromId.replace(/_/g, '-')) {
            confidence = 60;
          }
          // 6. Partial match - least confidence
          else if (entryNameWithoutExt.includes(filenameFromId.split('_')[0]) || 
                  filenameFromId.includes(entryNameWithoutExt.split('_')[0])) {
            confidence = 30;
          }
          
          // Update best match if this entry has higher confidence
          if (confidence > 0 && (!bestMatch || confidence > bestMatch.confidence)) {
            bestMatch = { entry, confidence };
          }
        }
        
        // Use the best match if confidence is high enough
        if (bestMatch && bestMatch.confidence >= 60) {
          result.push({
            title: bestMatch.entry.name.replace(/\.[^/.]+$/, ""),
            file: bestMatch.entry.file
          });
          
          // Mark this entry as matched
          bestMatch.entry.id = videoId;
          bestMatch.entry.confidence = bestMatch.confidence;
          
          console.log(`Matched video ID ${videoId} to file: ${bestMatch.entry.name} (confidence: ${bestMatch.confidence}%)`);
        }
      }
      
      // Second pass: try lower confidence matches for any remaining unmatched IDs
      const remainingIds = videoIds.filter(id => 
        !result.some(r => r.file.name === fileEntries.find(e => e.id === id)?.name)
      );
      
      if (remainingIds.length > 0) {
        console.log(`Found ${result.length} videos with high confidence, trying lower confidence matching for remaining ${remainingIds.length} IDs`);
        
        for (const videoId of remainingIds) {
          let bestMatch: { entry: typeof fileEntries[0], confidence: number } | null = null;
          
          // Try to find the best remaining match with any confidence
          for (let j = 0; j < fileEntries.length; j++) {
            const entry = fileEntries[j];
            if (entry.id) continue; // Skip already matched entries
            
            // If we have no match yet, or this entry has a higher confidence, use it
            if (!bestMatch || (entry.confidence && entry.confidence > bestMatch.confidence)) {
              bestMatch = { entry, confidence: entry.confidence || 0 };
            }
          }
          
          // Use the best available match
          if (bestMatch) {
            result.push({
              title: bestMatch.entry.name.replace(/\.[^/.]+$/, ""),
              file: bestMatch.entry.file
            });
            
            // Mark as matched
            bestMatch.entry.id = videoId;
            console.log(`Lower confidence match for ID ${videoId}: ${bestMatch.entry.name} (confidence: ${bestMatch.confidence}%)`);
          }
        }
      }
      
      // If we still haven't matched all videos, add any remaining videos up to the requested count
      if (result.length < videoIds.length) {
        const unmatchedFiles = fileEntries.filter(entry => !entry.id);
        const neededCount = videoIds.length - result.length;
        
        console.log(`Still need ${neededCount} more videos, adding from ${unmatchedFiles.length} unmatched files`);
        
        // Add up to the needed count
        const additionalFiles = unmatchedFiles.slice(0, neededCount);
        for (const entry of additionalFiles) {
          result.push({
            title: entry.name.replace(/\.[^/.]+$/, ""),
            file: entry.file
          });
          console.log(`[Fallback] Adding file ${entry.name} to complete the set`);
        }
      }
      
      console.log(`Final result: Found ${result.length} matching files out of ${originalVideoIds.length} requested IDs`);
      console.log('Files being included in zip:', result.map(r => r.title));
      
      return result;
    } catch (error) {
      console.error('Error getting video files:', error);
      throw error;
    }
  }, [folderHandle]);

  // Return additional information including hasSelectedFolder and getVideoFiles
  return {
    videos,
    loading,
    error,
    selectClipoFolder,
    hasPermission: permissionStatus === 'granted',
    lastFolderName,
    hasSelectedFolder,
    cancelBackgroundTasks,
    getVideoFiles // Add this function to the return value
  };
};

export default useLocalVideos;